import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";

import {
  DEFAULT_INSTANCE_BRANDING,
  readPublicInstanceBootstrap,
  type PublicInstanceBootstrap,
} from "@/lib/instance-platform";
import { applyInstanceBranding, clearInstanceBranding } from "@/lib/instance-branding";
import "@/components/instance/instance-branding.css";

type InstanceBrandingContextValue = {
  bootstrap: PublicInstanceBootstrap | null;
  loading: boolean;
  error: Error | null;
  reload: () => void;
};

const InstanceBrandingContext = createContext<InstanceBrandingContextValue>({
  bootstrap: null,
  loading: false,
  error: null,
  reload: () => undefined,
});

export function InstanceBrandingProvider({
  children,
  required = false,
}: {
  children: ReactNode;
  required?: boolean;
}) {
  const [bootstrap, setBootstrap] = useState<PublicInstanceBootstrap | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    let disposed = false;
    let timedOut = false;
    const timeout = window.setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, 10_000);
    setLoading(true);
    setError(null);
    readPublicInstanceBootstrap(controller.signal)
      .then((manifest) => {
        if (disposed) return;
        // Apply synchronously before exposing the routed application, avoiding a
        // frame rendered with another company's colours or assets.
        applyInstanceBranding(manifest);
        setBootstrap(manifest);
      })
      .catch((nextError: unknown) => {
        if (disposed) return;
        clearInstanceBranding();
        setBootstrap(null);
        setError(
          timedOut
            ? new Error("The company wallet configuration request timed out.")
            : nextError instanceof Error
              ? nextError
              : new Error("Instance branding failed"),
        );
      })
      .finally(() => {
        window.clearTimeout(timeout);
        if (!disposed) setLoading(false);
      });
    return () => {
      disposed = true;
      window.clearTimeout(timeout);
      controller.abort();
    };
  }, [attempt]);

  const value = useMemo<InstanceBrandingContextValue>(
    () => ({
      bootstrap,
      loading,
      error,
      reload: () => setAttempt((value) => value + 1),
    }),
    [bootstrap, error, loading],
  );

  if (loading) return <InstanceBootstrapScreen />;
  if (required && error) {
    return <InstanceBootstrapError error={error} retry={value.reload} />;
  }
  return (
    <InstanceBrandingContext.Provider value={value}>{children}</InstanceBrandingContext.Provider>
  );
}

export function useInstanceBranding() {
  return useContext(InstanceBrandingContext);
}

export function InstanceBrandLockup({ compact = false }: { compact?: boolean }) {
  const { bootstrap } = useInstanceBranding();
  const name = bootstrap?.display_name || "GENRL";
  const logo = bootstrap?.branding.logo_url;
  return (
    <span className="instance-brand-lockup" aria-label={`${name} home`}>
      <span
        className={`instance-brand-logo${logo ? " instance-brand-logo-image" : ""}`}
        aria-hidden="true"
      >
        {logo ? <img src={logo} alt="" /> : name.slice(0, 1).toUpperCase()}
      </span>
      {!compact ? (
        <span className="instance-brand-copy">
          <strong>{name}</strong>
          <small>Wallet &amp; compute</small>
        </span>
      ) : null}
    </span>
  );
}

function InstanceBootstrapScreen() {
  const style = {
    "--instance-background-color": DEFAULT_INSTANCE_BRANDING.background_color,
    "--instance-primary-color": DEFAULT_INSTANCE_BRANDING.primary_color,
  } as CSSProperties;
  return (
    <div className="instance-bootstrap-screen" style={style} aria-label="Loading company wallet">
      <span className="instance-bootstrap-mark">G</span>
      <span className="instance-bootstrap-pulse" />
      <span className="instance-bootstrap-pulse instance-bootstrap-pulse-short" />
    </div>
  );
}

function InstanceBootstrapError({ error, retry }: { error: Error; retry: () => void }) {
  return (
    <main className="instance-bootstrap-screen instance-bootstrap-error" role="alert">
      <span className="instance-bootstrap-mark">!</span>
      <h1>Wallet configuration unavailable</h1>
      <p>{error.message}</p>
      <button type="button" onClick={retry}>
        Try again
      </button>
    </main>
  );
}
